import { ref, reactive, computed, onBeforeUnmount } from 'vue'
import { BleClient, numbersToDataView } from '@capacitor-community/bluetooth-le'
import Memory from '@/lib/memory'
import { exportAndSaveCsv } from '@/lib/helpers/export'

// BLE Service UUIDs
const RECORDING_SERVICE = '0193d535-1813-7f13-bb6a-58e7a9aaacd0'
const BATTERY_SERVICE = '0000180f-0000-1000-8000-00805f9b34fb'
const SD_SERVICE = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c0'
const STATE_SERVICE = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c0'
const JACKET_SERVICE = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c0'

// BLE Characteristic UUIDs
const COMMAND_WRITE_CHARACTERISTIC = '0193d535-1813-7f13-bb6a-58e7a9aaacd1'
const SESSIONS_HEADER_CHARACTERISTIC = '0193d535-1813-7f13-bb6a-58e7a9aaacd2'
const ALL_SESSIONS_DATA_CHARACTERISTIC = '0193d535-1813-7f13-bb6a-58e7a9aaacd3'
const SINGLE_SESSION_DATA_CHARACTERISTIC = '0193d535-1813-7f13-bb6a-58e7a9aaacd4'
const ERASE_MEMORY_CHARACTERISTIC = '0193d535-1813-7f13-bb6a-58e7a9aaacd5'
const BATTERY_CHARACTERISTIC = '00002a19-0000-1000-8000-00805f9b34fb'
const SD_CHARACTERISTIC = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c1'
const STATE_CHARACTERISTIC = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c2'
const GPS_FIX_CHARACTERISTIC = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c3'
const JACKET_CONNECTION_CHARACTERISTIC = 'bb5db72a-03b3-40eb-9053-1098bf8ce0c4'

// BLE Commands - Must be sent as integers
const COMMAND_GET_SESSIONS_HEADER = 2
const COMMAND_GET_ALL_SESSIONS_DATA = 3
const COMMAND_READ_SESSION_BY_ID = 4 // Requires 2 bytes: [4, sessionId]
const COMMAND_DELETE_ALL_SESSIONS = 5
const COMMAND_START_RECORDING = 6
const COMMAND_STOP_RECORDING = 7
const COMMAND_CHECK_SADDLE_CONNECTION = 8
const COMMAND_GET_GPS_AND_TIME = 9 // Returns Unix time + GPS fix + satellites
const COMMAND_STOP_DOWNLOAD = 10
const COMMAND_STANDBY = 11
const COMMAND_USB_UPDATE = 12

const START_RECORDING_RESPONSE_TIMEOUT_MS = 5000

// Data packet sizes
const SESSION_HEADER_SIZE = 31
const SESSION_JACKET_SIZE = 28
const SESSION_SADDLE_SIZE = 16

// Device main states
const DEVICE_STATES = {
    IDLE: 0,
    ACQUISITION: 1,
    PLUGGED: 2,
    APP_CONNECT: 3,
    CHARGING: 4,
    SLEEP: 5,
    ERROR: 6,
}

// Acquisition sub-states
const ACQUISITION_SUB_STATES = {
    CHECK_SYNC: 0,
    WAIT_CONNECTION: 1,
    WAIT_STREAM_READY: 2,
    STREAMING: 3,
    STOP_STREAM: 4,
    WAIT_DISCONNECTION: 5,
    TRY_RECONNECT: 6,
    ERROR: 7,
}

// Plugged sub-states
const PLUGGED_SUB_STATES = {
    RESET: 0,
    SEND_TIME: 1,
    SEND_MAC: 2,
    GET_INFO: 3,
}

// App connection sub-states
const APP_SUB_STATES = {
    ADVERTISING: 0,
    CONNECTED_WAIT_CMD: 1,
    DATA_TRANSFER: 2,
    DISCONNECT_REQUESTED: 3,
}

// Sleep sub-states
const SLEEP_SUB_STATES = {
    WAITING: 0,
    START: 1,
}

/**
 * Main Freejump BLE composable for Vue.js
 * Handles all BLE communication with Freejump devices (jacket and saddle sensors)
 * @returns {Object} Reactive properties and methods for device interaction
 */
export default () => {
    // ==================== REACTIVE STATE ====================

    /**
     * Device properties updated via BLE notifications
     */
    const properties = reactive({
        battery: 0,
        batterySaddle: 0,
        name: '',
        startAt: null,
        freeSpace: null, // GB
        state: null,
        gpsFix: false,
        satellites: 0,
        saddleConnected: false,
        unixTime: null,
        lastStartRecordingStatus: null,
    })

    /**
     * Array of fetched recording sessions
     */
    const fetchedSessions = reactive([])

    /**
     * Connection state management
     */
    const isBusy = ref(false)
    const isConnected = ref(false)
    const lastConnectedId = ref(undefined)
    const connectionError = ref(null)

    // ==================== COMPUTED PROPERTIES ====================

    /**
     * Device identification and state checks
     */
    const isFreejump = computed(() => properties.name?.toLowerCase().includes('fjv'))
    const isRecording = computed(
        () =>
            properties.state?.main === DEVICE_STATES.ACQUISITION &&
            properties.state?.sub === ACQUISITION_SUB_STATES.STREAMING
    )
    const hasRecord = computed(() => fetchedSessions.length > 0)
    const isReadyToFetchRecord = computed(() => hasRecord.value && !isBusy.value)
    const isCharging = computed(() => properties.state?.main === DEVICE_STATES.CHARGING)
    const isInError = computed(() => properties.state?.main === DEVICE_STATES.ERROR)
    const isSleeping = computed(() => properties.state?.main === DEVICE_STATES.SLEEP)
    const isIdle = computed(() => properties.state?.main === DEVICE_STATES.IDLE)

    /**
     * Human-readable device state description
     */
    const deviceStateDescription = computed(() => {
        if (!properties.state) return 'Unknown'
        const main = properties.state.main
        const sub = properties.state.sub

        switch (main) {
            case DEVICE_STATES.IDLE:
                return 'Idle'
            case DEVICE_STATES.ACQUISITION:
                switch (sub) {
                    case ACQUISITION_SUB_STATES.CHECK_SYNC:
                        return 'Checking sync'
                    case ACQUISITION_SUB_STATES.WAIT_CONNECTION:
                        return 'Waiting for connection'
                    case ACQUISITION_SUB_STATES.WAIT_STREAM_READY:
                        return 'Preparing stream'
                    case ACQUISITION_SUB_STATES.STREAMING:
                        return 'Recording'
                    case ACQUISITION_SUB_STATES.STOP_STREAM:
                        return 'Stopping recording'
                    case ACQUISITION_SUB_STATES.WAIT_DISCONNECTION:
                        return 'Disconnecting'
                    case ACQUISITION_SUB_STATES.TRY_RECONNECT:
                        return 'Trying to reconnect'
                    case ACQUISITION_SUB_STATES.ERROR:
                        return 'Acquisition error'
                    default:
                        return 'Acquisition'
                }
            case DEVICE_STATES.PLUGGED:
                switch (sub) {
                    case PLUGGED_SUB_STATES.RESET:
                        return 'Resetting'
                    case PLUGGED_SUB_STATES.SEND_TIME:
                        return 'Sending time'
                    case PLUGGED_SUB_STATES.SEND_MAC:
                        return 'Sending MAC address'
                    case PLUGGED_SUB_STATES.GET_INFO:
                        return 'Getting info'
                    default:
                        return 'Plugged'
                }
            case DEVICE_STATES.APP_CONNECT:
                switch (sub) {
                    case APP_SUB_STATES.ADVERTISING:
                        return 'BLE advertising'
                    case APP_SUB_STATES.CONNECTED_WAIT_CMD:
                        return 'Connected - Waiting'
                    case APP_SUB_STATES.DATA_TRANSFER:
                        return 'Data transfer'
                    case APP_SUB_STATES.DISCONNECT_REQUESTED:
                        return 'Disconnect requested'
                    default:
                        return 'App connected'
                }
            case DEVICE_STATES.CHARGING:
                return 'Charging'
            case DEVICE_STATES.SLEEP:
                return sub === SLEEP_SUB_STATES.WAITING ? 'Going to sleep...' : 'Sleeping'
            case DEVICE_STATES.ERROR:
                return 'Error'
            default:
                return 'Unknown state'
        }
    })

    // ==================== INTERNAL VARIABLES ====================

    let device = undefined
    let activeSessionId = null
    let receivedBytesTotal = 0
    let sessionDataBuffer = []
    let sessionDataBufferLength = 0
    let sessionDataTimeout = null
    let sessionHeadersBuffer = []
    let sessionHeadersBufferLength = 0
    let sessionHeaderTimeout = null
    let statusRefreshInterval = null
    let startRecordingResponseResolver = null
    let startRecordingResponseRejecter = null
    let startRecordingResponseTimeout = null

    // ==================== BUFFER MANAGEMENT FUNCTIONS ====================

    /**
     * Reset session headers buffer and clear timeout
     */
    const resetSessionHeadersBuffer = () => {
        console.log('[BLE] Resetting session header buffer')
        sessionHeadersBuffer = []
        sessionHeadersBufferLength = 0
        if (sessionHeaderTimeout) {
            clearTimeout(sessionHeaderTimeout)
            sessionHeaderTimeout = null
        }
    }

    /**
     * Reset session data buffer and clear timeout
     */
    const resetSessionDataBuffer = () => {
        console.log('[BLE] Resetting session data buffer')
        sessionDataBuffer = []
        sessionDataBufferLength = 0
        if (sessionDataTimeout) {
            clearTimeout(sessionDataTimeout)
            sessionDataTimeout = null
        }
    }

    // ==================== COMMAND RESPONSE HELPERS ====================

    const clearStartRecordingResponseWait = () => {
        if (startRecordingResponseTimeout) {
            clearTimeout(startRecordingResponseTimeout)
            startRecordingResponseTimeout = null
        }
        startRecordingResponseResolver = null
        startRecordingResponseRejecter = null
    }

    const rejectStartRecordingResponseWait = (error) => {
        if (startRecordingResponseRejecter) {
            startRecordingResponseRejecter(error)
        }
        clearStartRecordingResponseWait()
    }

    const waitForStartRecordingResponse = () => {
        clearStartRecordingResponseWait()
        properties.lastStartRecordingStatus = null

        return new Promise((resolve, reject) => {
            startRecordingResponseResolver = resolve
            startRecordingResponseRejecter = reject
            startRecordingResponseTimeout = setTimeout(() => {
                rejectStartRecordingResponseWait(
                    new Error('Start recording response timeout')
                )
            }, START_RECORDING_RESPONSE_TIMEOUT_MS)
        })
    }

    const handleStartRecordingResponse = (value) => {
        if (!value || value.byteLength === 0) {
            return
        }

        const status = value.getUint8(0)
        properties.lastStartRecordingStatus = status

        if (status !== 0 && status !== 1 && status !== 2) {
            console.log(`[BLE] Unhandled command response status: ${status}`)
            if (startRecordingResponseResolver) {
                startRecordingResponseResolver(status)
                clearStartRecordingResponseWait()
            }
            return
        }

        if (startRecordingResponseResolver) {
            startRecordingResponseResolver(status)
            clearStartRecordingResponseWait()
        }
    }

    /**
     * Reset current active session tracking
     */
    const resetCurrentSession = () => {
        activeSessionId = null
        receivedBytesTotal = 0
    }

    // ==================== UTILITY FUNCTIONS ====================

    /**
     * Concatenate multiple Uint8Array buffers into a single array
     * @param {Array<Uint8Array>} arrays - Array of Uint8Arrays to concatenate
     * @returns {Uint8Array} Concatenated array
     */
    const concatUint8Arrays = (arrays) => {
        const totalLength = arrays.reduce((acc, curr) => acc + curr.length, 0)
        const result = new Uint8Array(totalLength)
        let offset = 0
        for (const arr of arrays) {
            result.set(arr, offset)
            offset += arr.length
        }
        return result
    }

    // ==================== SESSION MANAGEMENT ====================

    /**
     * Update or insert session header in the fetched sessions list
     * @param {Object} header - Session header data
     */
    const upsertSessionHeader = (header) => {
        try {
            const existing = fetchedSessions.find((s) => s.id === header.id)
            if (existing) {
                const prevSize = existing.header?.fileSize
                const newSize = header?.fileSize
                if (prevSize !== newSize) {
                    console.log(`[BLE] Session ${header.id} size updated: ${prevSize} → ${newSize}`)
                }
                existing.header = header
            } else {
                fetchedSessions.push({
                    id: header.id,
                    header,
                    records: [],
                    downloading: false,
                    progress: 0,
                    ready: false,
                })
                console.log(`[BLE] New session added: ID ${header.id}`)
            }
        } catch (err) {
            console.error('[BLE] Failed to upsert session header:', err)
        }
    }

    // ==================== DATA PARSING FUNCTIONS ====================

    /**
     * Parse session header from DataView
     * Format: 31 bytes containing session metadata
     * @param {DataView} dv - DataView containing the header data
     * @param {number} offset - Byte offset to start parsing from
     * @returns {Object|null} Parsed session header or null if invalid
     */
    const parseSessionRecord = (dv, offset = 0) => {
        if (dv.byteLength - offset < SESSION_HEADER_SIZE) return null

        try {
            const id = dv.getUint8(offset)
            const startTime = Number(dv.getBigUint64(offset + 1, false)) // MSB format
            const vesteVersion = {
                major: dv.getUint8(offset + 9),
                minor: dv.getUint8(offset + 10),
                patch: dv.getUint8(offset + 11),
            }
            const macVeste = Array.from({ length: 6 }, (_, i) => dv.getUint8(offset + 12 + i))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join(':')
            const selleVersion = {
                major: dv.getUint8(offset + 18),
                minor: dv.getUint8(offset + 19),
                patch: dv.getUint8(offset + 20),
            }
            const macSelle = Array.from({ length: 6 }, (_, i) => dv.getUint8(offset + 21 + i))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join(':')
            const fileSize = dv.getUint32(offset + 27, false) // MSB format

            return { id, startTime, vesteVersion, macVeste, selleVersion, macSelle, fileSize }
        } catch (err) {
            console.error('[BLE] Failed to parse session record:', err)
            return null
        }
    }

    /**
     * Decode a single IMU/GPS record from binary data
     * @param {DataView} dv - DataView containing the record
     * @param {number} offset - Byte offset to start reading from
     * @param {number} flag - 0 for jacket data (with GPS), 1 for saddle data (IMU only)
     * @returns {Object|null} Decoded record with sensor values
     */
    const decodeRecord = (dv, offset = 0, flag = 0) => {
        try {
            const record = {
                timestamp: dv.getUint32(offset, true), // LSB format
                source: flag === 0 ? 'veste' : 'selle',
                // Gyroscope values in tenths of mdps (converted to mdps)
                gyroX: dv.getInt16(offset + 4, true) / 10,
                gyroY: dv.getInt16(offset + 6, true) / 10,
                gyroZ: dv.getInt16(offset + 8, true) / 10,
                // Accelerometer values in tenths of mG (converted to mG)
                accX: dv.getInt16(offset + 10, true) / 10,
                accY: dv.getInt16(offset + 12, true) / 10,
                accZ: dv.getInt16(offset + 14, true) / 10,
                longitude: null,
                latitude: null,
                altitude: null,
            }

            // GPS data only available for jacket (flag = 0)
            if (flag === 0 && dv.byteLength - offset >= SESSION_JACKET_SIZE) {
                // GPS values provided as float32 decimal degrees by firmware
                record.longitude = dv.getFloat32(offset + 16, true)
                record.latitude = dv.getFloat32(offset + 20, true)
                record.altitude = dv.getInt32(offset + 24, true) / 1000
            }

            return record
        } catch (err) {
            console.error('[BLE] Failed to decode record:', err)
            return null
        }
    }

    /**
     * Decode a complete block of sensor records
     * @param {Uint8Array} buffer - Buffer containing multiple records
     * @param {number} flag - 0 for jacket, 1 for saddle
     * @returns {Array} Array of decoded records
     */
    const decodeCompleteBlock = (buffer, flag) => {
        const dv = new DataView(buffer.buffer)
        const records = []
        const step = flag === 0 ? SESSION_JACKET_SIZE : SESSION_SADDLE_SIZE
        let offset = 0

        while (offset + step <= dv.byteLength) {
            try {
                const record = decodeRecord(dv, offset, flag)
                if (record) records.push(record)
            } catch (err) {
                console.error(`[BLE] Failed to decode record at offset ${offset}:`, err)
            }
            offset += step
        }

        return records
    }

    // ==================== BLE NOTIFICATION HANDLERS ====================

    /**
     * Handle incoming session header notifications
     * Accumulates partial data until complete headers are received
     * @param {DataView} value - Notification data
     */
    const sessionHeaderNotificationHandler = (value) => {
        console.log('[BLE] Session header notification received')
        try {
            // Reset timeout for incomplete data
            if (sessionHeaderTimeout) clearTimeout(sessionHeaderTimeout)
            sessionHeaderTimeout = setTimeout(resetSessionHeadersBuffer, 5000)

            // Accumulate incoming data
            const chunk = new Uint8Array(value.buffer)
            sessionHeadersBuffer.push(chunk)
            sessionHeadersBufferLength += chunk.byteLength

            // Try to parse complete headers
            const combined = concatUint8Arrays(sessionHeadersBuffer)
            let offset = 0

            while (offset + SESSION_HEADER_SIZE <= combined.byteLength) {
                const dv = new DataView(combined.buffer, offset, SESSION_HEADER_SIZE)
                const record = parseSessionRecord(dv)
                if (record) upsertSessionHeader(record)
                offset += SESSION_HEADER_SIZE
            }

            // Keep any incomplete data for next notification
            if (offset < combined.byteLength) {
                const leftover = combined.slice(offset)
                sessionHeadersBuffer = [leftover]
                sessionHeadersBufferLength = leftover.byteLength
            } else {
                sessionHeadersBuffer = []
                sessionHeadersBufferLength = 0
            }
        } catch (err) {
            console.error('[BLE] Failed to process session header:', err)
        }
    }

    /**
     * Handle incoming session data notifications
     * Processes both header and data blocks for session downloads
     * @param {DataView} dv - Notification data
     */
    const sessionDataNotificationHandler = (dv) => {
        console.log('[BLE] Session data notification received')

        // Reset timeout for incomplete data
        if (sessionDataTimeout) clearTimeout(sessionDataTimeout)
        sessionDataTimeout = setTimeout(resetSessionDataBuffer, 500)

        // Check if this is the start of a new session (contains header)
        if (!activeSessionId && dv.byteLength >= SESSION_HEADER_SIZE) {
            const header = parseSessionRecord(dv)
            if (header) {
                upsertSessionHeader(header)
                activeSessionId = header.id
                receivedBytesTotal = 0
                console.log(`[BLE] Starting download for session ${activeSessionId}`)
            }
            return
        }

        // Accumulate data chunks
        const chunk = new Uint8Array(dv.buffer)
        sessionDataBuffer.push(chunk)
        sessionDataBufferLength += chunk.byteLength

        // Process accumulated buffer
        const combined = concatUint8Arrays(sessionDataBuffer)
        let offset = 0

        // Parse data blocks (format: 2 bytes length + 1 byte flag + data)
        while (offset + 3 <= combined.byteLength) {
            const expectedLength = (combined[offset] << 8) | combined[offset + 1]
            const flag = combined[offset + 2]
            const blockStart = offset + 3

            // Wait for complete block
            if (blockStart + expectedLength > combined.byteLength) break

            // Decode the block
            const blockData = combined.slice(blockStart, blockStart + expectedLength)
            const decodedRecords = decodeCompleteBlock(blockData, flag)

            // Add records to session
            const session = fetchedSessions.find((s) => s.id === activeSessionId)
            if (session) {
                session.records.push(...decodedRecords)
                receivedBytesTotal += expectedLength

                // Update progress
                if (session.header?.fileSize) {
                    const progress = Math.min(100, Math.floor((receivedBytesTotal / session.header.fileSize) * 100))
                    session.progress = progress
                    console.log(`[BLE] Session ${activeSessionId} progress: ${progress}%`)
                }
            }

            offset = blockStart + expectedLength
        }

        // Keep any incomplete data for next notification
        if (offset < combined.byteLength) {
            const leftover = combined.slice(offset)
            sessionDataBuffer = [leftover]
            sessionDataBufferLength = leftover.byteLength
        } else {
            sessionDataBuffer = []
            sessionDataBufferLength = 0
        }

        // Check if download is complete
        const session = fetchedSessions.find((s) => s.id === activeSessionId)
        if (session?.header?.fileSize && receivedBytesTotal >= session.header.fileSize) {
            console.log(`[BLE] Session ${activeSessionId} complete, exporting...`)
            session.downloading = false
            session.progress = 100
            session.ready = true
            exportSession(session)
            resetCurrentSession()
            resetSessionDataBuffer()
        }
    }

    // ==================== BLE NOTIFICATION SETUP ====================

    /**
     * Start all BLE notifications for device monitoring
     * Sets up listeners for battery, state, GPS, and data transfers
     */
    const startNotifications = async () => {
        console.log('[BLE] Starting notifications...')

        // Battery level notification (both jacket and saddle)
        await BleClient.startNotifications(device.deviceId, BATTERY_SERVICE, BATTERY_CHARACTERISTIC, (value) => {
            properties.battery = value.getUint8(1)
            properties.batterySaddle = value.getUint8(3)
            console.log(`[BLE] Battery: Jacket ${properties.battery}%, Saddle ${properties.batterySaddle}%`)
        })

        // SD card free space notification
        await BleClient.startNotifications(device.deviceId, SD_SERVICE, SD_CHARACTERISTIC, (value) => {
            properties.freeSpace = value.getUint32(0, false) // MSB format
            console.log(`[BLE] Free space: ${properties.freeSpace} GB`)
        })

        // Command responses (e.g., start recording acknowledgement)
        await BleClient.startNotifications(
            device.deviceId,
            RECORDING_SERVICE,
            COMMAND_WRITE_CHARACTERISTIC,
            handleStartRecordingResponse
        )

        // Device state notification
        await BleClient.startNotifications(device.deviceId, STATE_SERVICE, STATE_CHARACTERISTIC, (value) => {
            properties.state = {
                main: value.getUint8(0),
                sub: value.getUint8(1),
            }
            console.log(`[BLE] State: ${deviceStateDescription.value}`)
        })

        // Session headers notification
        await BleClient.startNotifications(
            device.deviceId,
            RECORDING_SERVICE,
            SESSIONS_HEADER_CHARACTERISTIC,
            sessionHeaderNotificationHandler
        )

        // Session data notifications (both all sessions and single session)
        await BleClient.startNotifications(
            device.deviceId,
            RECORDING_SERVICE,
            ALL_SESSIONS_DATA_CHARACTERISTIC,
            sessionDataNotificationHandler
        )

        await BleClient.startNotifications(
            device.deviceId,
            RECORDING_SERVICE,
            SINGLE_SESSION_DATA_CHARACTERISTIC,
            sessionDataNotificationHandler
        )

        // Saddle connection status notification (using corrected characteristic)
        await BleClient.startNotifications(
            device.deviceId,
            JACKET_SERVICE,
            JACKET_CONNECTION_CHARACTERISTIC,
            (value) => {
                properties.saddleConnected = value.getUint8(0) === 1
                console.log(`[BLE] Saddle connected: ${properties.saddleConnected}`)
            }
        )

        // GPS fix, Unix time and satellites notification (combined in CMD 9)
        await BleClient.startNotifications(device.deviceId, JACKET_SERVICE, GPS_FIX_CHARACTERISTIC, (value) => {
            // Format: 8 bytes Unix time + 1 byte GPS fix + 1 byte satellites (10 bytes total)
            if (value.byteLength >= 10) {
                // Unix timestamp (8 bytes, MSB)
                const unixTime = Number(value.getBigUint64(0, false))
                properties.unixTime = unixTime > 0 ? unixTime : null

                // GPS fix status (byte 8)
                properties.gpsFix = value.getUint8(8) === 1

                // Number of satellites (byte 9)
                properties.satellites = value.getUint8(9)

                console.log(
                    `[BLE] GPS/Time: Unix=${unixTime}, Fix=${properties.gpsFix}, Satellites=${properties.satellites}`
                )
            }
        })

        // Memory erase result notification
        await BleClient.startNotifications(device.deviceId, RECORDING_SERVICE, ERASE_MEMORY_CHARACTERISTIC, (value) => {
            const success = value.getUint8(0) === 1
            console.log(`[BLE] Memory erase: ${success ? 'Success' : 'Failed'}`)
        })

        console.log('[BLE] All notifications started')
    }

    /**
     * Stop all active BLE notifications
     * Called before disconnection to clean up listeners
     */
    const stopNotifications = async () => {
        console.log('[BLE] Stopping notifications...')
        const notificationsToStop = [
            { service: BATTERY_SERVICE, characteristic: BATTERY_CHARACTERISTIC },
            { service: SD_SERVICE, characteristic: SD_CHARACTERISTIC },
            { service: RECORDING_SERVICE, characteristic: COMMAND_WRITE_CHARACTERISTIC },
            { service: STATE_SERVICE, characteristic: STATE_CHARACTERISTIC },
            { service: RECORDING_SERVICE, characteristic: SESSIONS_HEADER_CHARACTERISTIC },
            { service: RECORDING_SERVICE, characteristic: ALL_SESSIONS_DATA_CHARACTERISTIC },
            { service: RECORDING_SERVICE, characteristic: SINGLE_SESSION_DATA_CHARACTERISTIC },
            { service: RECORDING_SERVICE, characteristic: ERASE_MEMORY_CHARACTERISTIC },
            { service: JACKET_SERVICE, characteristic: JACKET_CONNECTION_CHARACTERISTIC },
            { service: JACKET_SERVICE, characteristic: GPS_FIX_CHARACTERISTIC },
        ]

        for (const { service, characteristic } of notificationsToStop) {
            try {
                await BleClient.stopNotifications(device.deviceId, service, characteristic)
            } catch (err) {
                console.log(`[BLE] Failed to stop notification for ${characteristic}:`, err)
            }
        }
        console.log('[BLE] All notifications stopped')
    }

    // ==================== COMMAND FUNCTIONS ====================

    /**
     * Send a command to the device via BLE
     * @param {DataView} cmd - Command data to send
     * @throws {Error} If no device is connected
     */
    const sendCommand = async (cmd) => {
        console.log('[BLE] Sending command:', cmd)
        if (!device?.deviceId) {
            throw new Error('[BLE] No device connected')
        }

        try {
            await BleClient.write(device.deviceId, RECORDING_SERVICE, COMMAND_WRITE_CHARACTERISTIC, cmd)
            console.log('[BLE] Command sent successfully')
        } catch (err) {
            console.error('[BLE] Failed to send command:', err)
            throw err
        }
    }

    // ==================== PUBLIC COMMAND METHODS ====================

    /**
     * Retrieve the list of all recorded sessions from device
     * Clears existing sessions and requests headers
     */
    const getSessionsList = async () => {
        console.log('[BLE] Getting sessions list...')
        fetchedSessions.splice(0)
        await sendCommand(numbersToDataView([COMMAND_GET_SESSIONS_HEADER]))
    }

    /**
     * Download data for all sessions from device
     * Marks all sessions as downloading and requests their data
     */
    const getAllSessionsData = async () => {
        console.log('[BLE] Getting all sessions data...')
        resetCurrentSession()
        fetchedSessions.forEach((session) => {
            session.records = []
            session.downloading = true
            session.progress = 0
            session.ready = false
        })
        await sendCommand(numbersToDataView([COMMAND_GET_ALL_SESSIONS_DATA]))
    }

    /**
     * Download data for a specific session
     * @param {number} sessionId - ID of the session to download
     */
    const getSessionById = async (sessionId) => {
        console.log(`[BLE] Getting session ${sessionId} data...`)
        resetCurrentSession()
        const session = fetchedSessions.find((s) => s.id === sessionId)
        if (session) {
            session.records = []
            session.downloading = true
            session.progress = 0
            session.ready = false
        }
        await sendCommand(numbersToDataView([COMMAND_READ_SESSION_BY_ID, sessionId]))
    }

    /**
     * Start a new recording session
     * Device must have saddle connected for recording
     * Returns: 1 if successful, 0 if saddle not synced, 2 if 30 files limit reached
     */
    const startRecording = async () => {
        console.log('[BLE] Starting recording...')

        // Send command and get response
        const responsePromise = waitForStartRecordingResponse()
        try {
            await sendCommand(numbersToDataView([COMMAND_START_RECORDING]))
        } catch (error) {
            rejectStartRecordingResponseWait(error)
            await responsePromise.catch(() => {})
            console.error('[BLE] Failed to send start recording command:', error)
            throw error
        }

        try {
            const status = await responsePromise

            if (status === 1) {
                properties.startAt = Date.now()
            } else {
                properties.startAt = null
                console.warn(`[BLE] Recording did not start. Status=${status}`)
            }

            return status
        } catch (error) {
            console.error('[BLE] Failed to receive start recording response:', error)
            throw error
        }
    }

    /**
     * Stop the current recording session
     */
    const stopRecording = async () => {
        console.log('[BLE] Stopping recording...')
        await sendCommand(numbersToDataView([COMMAND_STOP_RECORDING]))
    }

    /**
     * Erase all sessions from device memory
     * WARNING: This action cannot be undone
     */
    const eraseMemory = async () => {
        console.log('[BLE] Erasing memory...')
        await sendCommand(numbersToDataView([COMMAND_DELETE_ALL_SESSIONS]))
    }

    /**
     * Stop any ongoing download operation
     */
    const stopDownload = async () => {
        console.log('[BLE] Stopping download...')
        await sendCommand(numbersToDataView([COMMAND_STOP_DOWNLOAD]))
        resetCurrentSession()
        resetSessionDataBuffer()
    }

    /**
     * Put device into standby mode
     */
    const goToStandby = async () => {
        console.log('[BLE] Going to standby...')
        await sendCommand(numbersToDataView([COMMAND_STANDBY]))
    }

    /**
     * Check if saddle is connected to jacket
     */
    const checkSaddleConnection = async () => {
        console.log('[BLE] Checking saddle connection...')
        await sendCommand(numbersToDataView([COMMAND_CHECK_SADDLE_CONNECTION]))
    }

    /**
     * Request GPS status, Unix time and satellite count (combined command)
     */
    const getGpsAndTimeStatus = async () => {
        console.log('[BLE] Getting GPS and time status...')
        await sendCommand(numbersToDataView([COMMAND_GET_GPS_AND_TIME]))
    }

    /**
     * Trigger USB update mode on device
     */
    const triggerUsbUpdate = async () => {
        console.log('[BLE] Triggering USB update...')
        await sendCommand(numbersToDataView([COMMAND_USB_UPDATE]))
    }

    // ==================== DATA EXPORT ====================

    /**
     * Export session data to CSV file
     * @param {Object} session - Session object containing records to export
     */
    const exportSession = async (session) => {
        const timestamp = session?.header?.startTime > 0 ? session.header.startTime * 1000 : Date.now()
        const filename = `fj-${session.id}-${new Date(timestamp).toISOString().replace(/[:.]/g, '-')}`

        // Format records for CSV with proper units
        const formattedRecords = session.records.map((record) => ({
            timestamp: record.timestamp,
            source: record.source,
            gyro_x_mdps: record.gyroX,
            gyro_y_mdps: record.gyroY,
            gyro_z_mdps: record.gyroZ,
            acc_x_mg: record.accX,
            acc_y_mg: record.accY,
            acc_z_mg: record.accZ,
            longitude: record.longitude,
            latitude: record.latitude,
            altitude: record.altitude,
        }))

        exportAndSaveCsv(formattedRecords, filename)
        console.log(`[BLE] Session ${session.id} exported: ${filename}`)
    }

    // ==================== CONNECTION MANAGEMENT ====================

    /**
     * Initiate connection to a Freejump device
     * Scans for devices with 'FJV' prefix and connects to selected one
     */
    const connect = async () => {
        console.log('[BLE] Starting connection process...')
        connectionError.value = null

        // Stop any ongoing scan
        try {
            await BleClient.stopLEScan()
        } catch {}

        try {
            const newDevice = await BleClient.requestDevice({
                namePrefix: 'FJV',
                optionalServices: [BATTERY_SERVICE, RECORDING_SERVICE, SD_SERVICE, STATE_SERVICE, JACKET_SERVICE],
            })
            await connectDevice(newDevice)
        } catch (err) {
            console.error('[BLE] Connection failed:', err)
            connectionError.value = err.message
            await disconnect()
        }
    }

    /**
     * Connect to a specific BLE device
     * @param {Object} newDevice - Device object from BLE scan
     */
    const connectDevice = async (newDevice) => {
        if (isBusy.value) {
            console.log('[BLE] Already busy, skipping connection')
            return
        }

        isBusy.value = true
        device = newDevice
        lastConnectedId.value = device.deviceId
        await Memory.set('lastConnectedFreejumpId', device.deviceId)
        properties.name = device.name

        try {
            console.log(`[BLE] Connecting to ${device.name}...`)
            await BleClient.connect(device.deviceId, () => disconnect())

            await startNotifications()
            isConnected.value = true
            console.log(`[BLE] Connected to ${device.name}`)

            // Start periodic status refresh
            startStatusRefresh()
        } catch (err) {
            console.error('[BLE] Failed to connect to device:', err)
            await disconnect()
        } finally {
            isBusy.value = false
        }
    }

    /**
     * Disconnect from the current device
     * @param {boolean} forget - If true, removes saved device ID
     */
    const disconnect = async (forget = false) => {
        console.log('[BLE] Disconnecting...')
        isBusy.value = true

        // Stop periodic refresh
        stopStatusRefresh()

        // Reject any pending command responses
        rejectStartRecordingResponseWait(new Error('Device disconnected'))

        try {
            if (forget) {
                Memory.remove('lastConnectedFreejumpId')
                lastConnectedId.value = undefined
            }
            if (device?.deviceId) {
                await stopNotifications()
                await BleClient.disconnect(device.deviceId)
            }
        } catch (err) {
            console.error('[BLE] Error during disconnection:', err)
        } finally {
            // Reset all state
            device = undefined
            isConnected.value = false
            Object.assign(properties, {
                battery: 0,
                batterySaddle: 0,
                name: '',
                startAt: null,
                freeSpace: null,
                state: null,
                gpsFix: false,
                satellites: 0,
                saddleConnected: false,
                unixTime: null,
                lastStartRecordingStatus: null,
            })
            isBusy.value = false
        }
    }

    /**
     * Attempt to reconnect to the last connected device
     */
    const reconnectToLastDevice = async () => {
        const savedId = await Memory.get('lastConnectedFreejumpId')
        if (!savedId) {
            console.log('[BLE] No saved device to reconnect')
            return false
        }

        try {
            console.log('[BLE] Attempting to reconnect to saved device...')
            // Note: Direct reconnection by ID may not be supported on all platforms
            // May need to scan and match by ID
            await connect()
            return true
        } catch (err) {
            console.error('[BLE] Failed to reconnect:', err)
            return false
        }
    }

    // ==================== PERIODIC STATUS REFRESH ====================

    /**
     * Start periodic status refresh (GPS, time, saddle connection)
     * Runs every 5 seconds while connected
     */
    const startStatusRefresh = () => {
        console.log('[BLE] Starting periodic status refresh')

        // Clear any existing interval
        stopStatusRefresh()

        // Set up new interval
        statusRefreshInterval = setInterval(() => {
            if (isConnected.value) {
                console.log('[BLE] Running periodic status check')
                getGpsAndTimeStatus() // Combined command for GPS + time
                checkSaddleConnection()
            }
        }, 5000)
    }

    /**
     * Stop periodic status refresh
     */
    const stopStatusRefresh = () => {
        if (statusRefreshInterval) {
            console.log('[BLE] Stopping periodic status refresh')
            clearInterval(statusRefreshInterval)
            statusRefreshInterval = null
        }
    }

    // ==================== CLEANUP ====================

    /**
     * Cleanup function to be called when component unmounts
     * Ensures proper disconnection and cleanup
     */
    const cleanup = async () => {
        console.log('[BLE] Cleaning up...')
        stopStatusRefresh()
        if (isConnected.value) {
            await disconnect()
        }
    }

    // ==================== PUBLIC API ====================

    /**
     * Return public interface for the composable
     * Exposes reactive properties and methods for component use
     */
    return {
        // Reactive properties
        properties,
        isBusy,
        isConnected,
        isFreejump,
        isRecording,
        hasRecord,
        isReadyToFetchRecord,
        fetchedSessions,
        connectionError,
        deviceStateDescription,
        isCharging,
        isInError,
        isSleeping,
        isIdle,

        // Connection methods
        connect,
        disconnect,
        connectDevice,
        reconnectToLastDevice,

        // Session management methods
        getSessionsList,
        getAllSessionsData,
        getSessionById,
        exportSession,

        // Recording methods
        startRecording,
        stopRecording,

        // Device control methods
        stopDownload,
        eraseMemory,
        goToStandby,
        triggerUsbUpdate,

        // Status methods
        getGpsAndTimeStatus,
        checkSaddleConnection,

        // Cleanup
        cleanup,
    }
}
